// src/utils/orderPayload.ts

type OrderType = "exchpart" | "sellpart";

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  country: string;
  city: string;
  postal: string;
  address: string;
  comment: string;
  useDifferentAddress: boolean;
  receiverFirstName: string;
  receiverLastName: string;
  receiverPhone: string;
  receiverEmail: string;
  receiverCountry: string;
  receiverCity: string;
  receiverPostal: string;
  receiverAddress: string;
}

interface CartItem {
  goid: string;
  quantity: number;
  typeorder: OrderType;
  table: string;
}

// Функція для створення масиву одиничних товарів
export function createIndividualItems(cart: CartItem[]) {
  const individualItems: Array<{
    goid: string;
    quantity: number;
    typeorder: OrderType;
    table: string;
  }> = [];

  cart.forEach((item) => {
    for (let i = 0; i < item.quantity; i++) {
      individualItems.push({
        goid: String(item.goid),
        quantity: 1,
        typeorder: item.typeorder,
        table: item.table,
      });
    }
  });

  return individualItems;
}

// Формуємо payload з одиничними товарами
export function buildOrderPayload(formData: FormData, cart: CartItem[]) {
  const goods = createIndividualItems(cart);

  // Контакти для платника
  const contacts = [
    formData.phone.trim()
      ? {
          type: "mobilephone",
          value: formData.phone.trim().replace(/\s+/g, ""),
        }
      : null,
    formData.email.trim()
      ? { type: "email", value: formData.email.trim() }
      : null,
  ].filter(Boolean) as Array<{ type: string; value: string }>;

  // Контакти для отримувача
  const lcontacts = [
    formData.receiverPhone.trim()
      ? {
          type: "lmobilephone",
          value: formData.receiverPhone.trim().replace(/\s+/g, ""),
        }
      : null,
    formData.receiverEmail.trim()
      ? { type: "lemail", value: formData.receiverEmail.trim() }
      : null,
  ].filter(Boolean) as Array<{ type: string; value: string }>;

  return {
    type: "orderadd",
    success_url: "https://hixa.eu/payment_success",
    cancel_url: "https://hixa.eu/payment_failed",
    goods,
    description: (formData.comment || "").trim() || "-",
    first_name: formData.firstName.trim(),
    last_name: formData.lastName.trim(),
    contacts,
    country: formData.country,
    index: formData.postal.trim(),
    city: formData.city.trim(),
    address: formData.address.trim(),
    receiver: formData.useDifferentAddress
      ? {
          lfirstName: formData.receiverFirstName.trim(),
          llastName: formData.receiverLastName.trim(),
          lcontacts,
          laddress: formData.receiverAddress.trim(),
          lcity: formData.receiverCity.trim(),
          lpostal: formData.receiverPostal.trim(),
          lcountry: formData.receiverCountry.trim(),
        }
      : null,
  };
}

// Перекодуємо payload у x-www-form-urlencoded
export function toFormData(p: ReturnType<typeof buildOrderPayload>) {
  const params = new URLSearchParams();
  params.set("type", p.type);

  // Додаємо товари з параметром table
  p.goods.forEach((g, i) => {
    params.set(`goods[${i}][goid]`, String(g.goid));
    params.set(`goods[${i}][quantity]`, String(g.quantity));
    params.set(`goods[${i}][typeorder]`, g.typeorder);
    params.set(`goods[${i}][table]`, g.table);
  });

  // Додаємо основні дані
  params.set("description", p.description);
  params.set("first_name", p.first_name);
  params.set("last_name", p.last_name);

  // Додаємо контакти платника
  p.contacts.forEach((c, i) => {
    params.set(`contacts[${i}][type]`, c.type);
    params.set(`contacts[${i}][value]`, c.value);
  });

  // Додаємо основну адресу
  params.set("country", p.country);
  params.set("index", p.index);
  params.set("city", p.city);
  params.set("address", p.address);

  // Якщо є отримувач, додаємо його дані
  if (p.receiver) {
    params.set("receiver[first_name]", p.receiver.lfirstName);
    params.set("receiver[last_name]", p.receiver.llastName);

    // Додаємо контакти отримувача
    p.receiver.lcontacts.forEach((c, i) => {
      params.set(`receiver[lcontacts][${i}][type]`, c.type);
      params.set(`receiver[lcontacts][${i}][value]`, c.value);
    });

    // Додаємо адресу отримувача
    params.set("receiver[address]", p.receiver.laddress);
    params.set("receiver[city]", p.receiver.lcity);
    params.set("receiver[postal]", p.receiver.lpostal);
    params.set("receiver[country]", p.receiver.lcountry);
  }

  return params;
}
